import { createQuery } from "@nova/queryable"
import { useBatchPilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useMemo, useState } from "react"
import classNames from "classnames"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { Card } from "../MetricLayout"
import { pluralize } from "@nova/util-strings"
import { useRouter } from "pages"
import { springDelay } from "@nova/component-charts/src/utils/animationUtils"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"

function toDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

type WeekCell = { date: Date; hours: number; inYear: boolean }

function buildWeekGrid(year: number, dailyMap: Map<string, number>): WeekCell[][] {
    // Start from the Sunday on or before Jan 1
    const jan1 = new Date(year, 0, 1)
    const startDate = new Date(jan1)
    startDate.setDate(jan1.getDate() - jan1.getDay())

    // End at Saturday on or after Dec 31
    const dec31 = new Date(year, 11, 31)
    const endDate = new Date(dec31)
    endDate.setDate(dec31.getDate() + (6 - dec31.getDay()))

    const weeks: WeekCell[][] = []
    const cur = new Date(startDate)

    while (cur <= endDate) {
        const week: WeekCell[] = []
        for (let d = 0; d < 7; d++) {
            week.push({
                date: new Date(cur),
                hours: dailyMap.get(toDateKey(cur)) ?? 0,
                inYear: cur.getFullYear() === year,
            })
            cur.setDate(cur.getDate() + 1)
        }
        weeks.push(week)
    }

    return weeks
}

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"]

function getMonthLabels(weeks: WeekCell[][]): Array<{ label: string; colIndex: number }> {
    const labels: Array<{ label: string; colIndex: number }> = []
    let lastMonth = -1
    weeks.forEach((week, colIndex) => {
        const inYearCell = week.find((c) => c.inYear)
        if (!inYearCell) return
        const month = inYearCell.date.getMonth()
        if (month !== lastMonth) {
            labels.push({ label: MONTHS[month], colIndex })
            lastMonth = month
        }
    })
    return labels
}

const currentYear = new Date().getFullYear()
const yearOptions = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3]

export function DroneHoursSection() {
    const [selectedYear, setSelectedYear] = useState(currentYear)

    const yearStart = useMemo(() => new Date(selectedYear, 0, 1).toISOString(), [selectedYear])
    const yearEnd = useMemo(() => new Date(selectedYear + 1, 0, 1).toISOString(), [selectedYear])

    const queries = useMemo(
        () => ({
            dailyHours: createQuery({
                resource: "metadata",
                timeBuckets: [{ field: "started_at", unit: "day" as const, alias: "bucket" }],
                aggregates: [{ func: "SUM" as const, field: "airtime_hours", alias: "hours" }],
                filters: [
                    { field: "started_at", op: ">=" as const, value: yearStart },
                    { field: "started_at", op: "<" as const, value: yearEnd },
                ],
            }),
            topPilots: createQuery({
                resource: "metadata",
                select: ["mission.user_id"] as const,
                aggregates: [{ func: "SUM" as const, field: "airtime_hours", alias: "totalHours" }],
                filters: [
                    { field: "started_at", op: ">=" as const, value: yearStart },
                    { field: "started_at", op: "<" as const, value: yearEnd },
                ],
                orderBy: [{ field: "totalHours", dir: "DESC" as const }],
                limit: 3,
            }),
        }),
        [yearStart, yearEnd]
    )

    const { data, isLoading } = useBatchPilotTrackingQuery(queries)
    const users = useAppSelector(selectAllRegisteredMembers)

    const dailyMap = useMemo(() => {
        const map = new Map<string, number>()
        for (const row of data?.dailyHours ?? []) {
            if (!row.bucket) continue
            map.set(toDateKey(row.bucket), row.hours ?? 0)
        }
        return map
    }, [data?.dailyHours])

    const totalHours = useMemo(() => {
        let total = 0
        dailyMap.forEach((v) => {
            total += v
        })
        return total
    }, [dailyMap])

    const weeks = useMemo(() => buildWeekGrid(selectedYear, dailyMap), [selectedYear, dailyMap])
    const monthLabels = useMemo(() => getMonthLabels(weeks), [weeks])
    const maxHours = useMemo(() => {
        let max = 0
        dailyMap.forEach((v) => {
            if (v > max) max = v
        })
        return max
    }, [dailyMap])

    const topPilots = useMemo(
        () =>
            (data?.topPilots ?? []).map((p) => {
                const user = users?.find((u) => u.id === p["mission.user_id"])
                return {
                    userId: p["mission.user_id"],
                    fullName: user?.fullName ?? "Unknown Pilot",
                    hours: p.totalHours ?? 0,
                }
            }),
        [data?.topPilots, users]
    )

    return (
        <div className="flex flex-col gap-3">
            <div className="text-gray-50 text-[22px] telegraf">Total Drone Hours</div>
            <Card className="!p-8 !pb-4">
                <div className="flex gap-4">
                    <div className="flex flex-col gap-1 shrink-0">
                        {yearOptions.map((year) => (
                            <div
                                key={year}
                                className={classNames(
                                    "px-3 py-1.5 rounded-md transition-colors text-left cursor-pointer",
                                    {
                                        "bg-blue-900 text-white": selectedYear === year,
                                        "text-gray-300 hover:text-white": selectedYear !== year,
                                    }
                                )}
                                onClick={() => setSelectedYear(year)}
                            >
                                {year}
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col gap-4 grow overflow-hidden">
                        <Heatmap
                            animationKey={selectedYear}
                            monthLabels={monthLabels}
                            data={weeks.map((week) =>
                                week.map((cell) => {
                                    if (!cell.inYear) return null
                                    return {
                                        value: maxHours > 0 ? cell.hours / maxHours : 0,
                                        tooltip: cell.date.toDateString(),
                                    }
                                })
                            )}
                        />
                        <div className="w-full h-px bg-white-200" />
                        <div className="flex gap-4 min-h-[34px]">
                            <div className="flex gap-2 shrink-0">
                                <small className="xs text-gray-300">Total:</small>
                                <div className="font-medium">
                                    {isLoading
                                        ? "--"
                                        : pluralize({ count: Math.round(totalHours), str: "Hour" })}
                                </div>
                            </div>
                            <div className="grow" />
                            <div className="flex gap-4 flex-wrap">
                                {topPilots.map((pilot, i) => (
                                    <TopPilot key={pilot.userId} pilot={pilot} placement={i + 1} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    )
}

function TopPilot({
    pilot,
    placement,
}: {
    pilot: { userId: string | null; fullName: string; hours: number }
    placement: number
}) {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <div className="flex items-center gap-2">
            <img
                src={`/images/pilot-tracking-records/placement-${placement}.webp`}
                alt={`#${placement}`}
                className="w-8 shrink-0"
            />
            <div className="flex gap-3 items-center">
                <div className="w-8 h-8 rounded-md overflow-hidden shrink-0">
                    <ProfilePhoto userId={pilot.userId ?? ""} fullName={pilot.fullName} />
                </div>
                <div className="flex flex-col justify-between">
                    <router.Link
                        to={{
                            path: "/fleet/pilots/:pilotId",
                            params: { pilotId: pilot.userId ?? "" },
                        }}
                    >
                        <div className="text-white font-medium hover:text-blue-500 hover:underline">
                            {pilot.fullName}
                        </div>
                    </router.Link>
                    <small className="font-mono text-gray-300">
                        {pluralize({ count: Math.round(pilot.hours), str: "Hour" })}
                    </small>
                </div>
            </div>
        </div>
    )
}

const DAYS = ["S", "M", "T", "W", "T", "F", "S"]

type CellData = {
    value: number
    tooltip: string
}
const CELL_STEP_PX = 16
const DAY_LABEL_OFFSET_PX = 20

function Heatmap({
    data,
    monthLabels,
    animationKey,
}: {
    data: (CellData | null)[][]
    monthLabels: Array<{ label: string; colIndex: number }>
    animationKey: number
}) {
    const totalCells = data.length * 7

    return (
        <div className="flex flex-col gap-4 w-full">
            <div className="flex items-center justify-between">
                <div className="text-white text-lg telegraf">Activity</div>
                <div className="flex items-center gap-1 text-xs font-mono text-gray-300">
                    <small className="xs text-gray-300 block">LESS</small>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <HeatmapSquare key={i} value={i * 0.25} />
                    ))}
                    <small className="xs text-gray-300 block">MORE</small>
                </div>
            </div>

            <ScrollableArea horizontal className="flex-1">
                <div className="flex flex-col gap-1 w-fit" key={animationKey}>
                    <div
                        className="relative h-4 overflow-hidden"
                        style={{ marginLeft: DAY_LABEL_OFFSET_PX }}
                    >
                        {monthLabels.map(({ label, colIndex }) => (
                            <span
                                key={label}
                                className="absolute text-[10px] text-gray-300"
                                style={{ left: colIndex * CELL_STEP_PX }}
                            >
                                {label}
                            </span>
                        ))}
                    </div>

                    <div className="flex gap-1">
                        <div className="flex flex-col gap-1 shrink-0">
                            {DAYS.map((day, i) => (
                                <div
                                    key={i}
                                    className="w-3 h-3 flex items-center justify-center text-[10px] text-gray-300"
                                >
                                    {day}
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-1">
                            {data.map((col, ci) => (
                                <div key={ci} className="flex flex-col gap-1">
                                    {col.map((cell, ri) => {
                                        const cellIndex = ci * 7 + ri
                                        const delay = springDelay(cellIndex / totalCells, 900)
                                        return cell === null ? (
                                            <div key={ri} className="w-3 h-3" />
                                        ) : (
                                            <div key={ri} className="relative group/tooltip">
                                                <HeatmapSquare
                                                    value={cell.value}
                                                    animationDelay={delay}
                                                />
                                                <Tooltip
                                                    title={cell.tooltip}
                                                    placement={
                                                        ci < 3
                                                            ? "right"
                                                            : ci >= data.length - 5
                                                              ? "left"
                                                              : ri < 4
                                                                ? "bottom"
                                                                : "top"
                                                    }
                                                />
                                            </div>
                                        )
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </ScrollableArea>
        </div>
    )
}

const heatmapSquareStyles = [
    {
        backgroundColor: "rgba(255, 255, 255, 0.10)",
        boxShadow: "transparent",
    },
    {
        backgroundColor: "rgba(0, 249, 161, 0.20)",
        boxShadow: "transparent",
    },
    {
        backgroundColor: "rgba(0, 249, 161, 0.60)",
        boxShadow: "transparent",
    },
    {
        backgroundColor: "rgba(0, 249, 161, 0.80)",
        boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.40)",
    },
    {
        backgroundColor: "rgba(0, 249, 161, 1)",
        boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.70)",
    },
]
function HeatmapSquare({ value, animationDelay }: { value: number; animationDelay?: number }) {
    const normalized = Math.round(value * 4)
    const style = normalized > 0 ? heatmapSquareStyles[normalized] : null
    return (
        <div className="w-3 h-3 relative overflow-hidden">
            <div
                className="absolute inset-0.5 rounded-sm"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.10)" }}
            />
            {style && (
                <div
                    className="absolute inset-0.5 rounded-sm motion-safe:animate-pip-fill"
                    style={{ ...style, animationDelay: `${animationDelay ?? 0}ms` }}
                />
            )}
        </div>
    )
}
